import { Box, Paper, Tooltip, Typography } from "@mui/material";
import { Stack, useTheme } from "@mui/system";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { gql } from "generated-graphql";
import {
  ChemicalFacilitiesQuery,
  ChemicalFacilitiesQueryVariables,
  TierIiReportEncampStatus,
  TierIiReportKind,
  TierIiReportOrgStatus,
} from "generated-graphql/graphql";
import { useDebug } from "hooks/useDebug";
import { useOmnisearchDatagrid } from "hooks/useOmnisearchDatagrid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilterGroup } from "../../../../components/Omnisearch/FilterPopover";
import { OmnisearchDataGrid } from "../../../../components/OmnisearchDataGrid";
import { ChemicalFacilitiesActionsMenu } from "./ChemicalFacilitiesActionsMenu";
import { ExportGridToCsvButton } from "./ExportGridToCsvButton";

const CHEMICAL_FACILITIES_QUERY = gql(`
  query ChemicalFacilities($search: String, $page: Int, $pageSize: Int, $sort: [SortModel!]) {
    chemicalFacilities(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
      items {
        id
        facilityId
        tierIIReportId
        organization
        facilityName
        streetAddress1
        streetAddress2
        city
        zip
        state
        customerFacilityId
        organizationStatus
        encampStatus
        reportKind
        chemicalCount
        partnerTenantName
        issues
        county
        fireDepartment
        lepc
        hasIncompleteReviews
        fulfillmentNote
        csmName
        csmEmail
        amName
        amEmail
        dataManagerName
        dataManagerEmail
        wasteManagerName
        wasteManagerEmail
        pocName
        pocEmail
      }
      count
    }
  }
`);

export type Row =
  ChemicalFacilitiesQuery["chemicalFacilities"]["items"][number];

function getReportStatusDisplay(
  organizationStatus: string | null | undefined,
  encampStatus: string | null | undefined
): string {
  // If both are null/undefined, show "No Report"
  if (!organizationStatus && !encampStatus) {
    return "⭕️ No Report";
  }

  // If org status is verified, show encamp status, otherwise show org status
  const statusToCheck =
    organizationStatus === "VERIFIED" && encampStatus
      ? encampStatus
      : organizationStatus;

  // Handle status mappings (both org and encamp statuses)
  switch (statusToCheck) {
    case null:
    case undefined:
      return "⭕️ No Report";
    // Organization status mappings
    case "NOT_STARTED":
      return "🔶 Not Started";
    case "ASSIGNED":
    case "IN_PROGRESS":
      return "🚧 In Progress";
    case "IN_REVIEW":
      return "🔍 In Review";
    case "VERIFIED":
      return "✅ Verified";
    case "NOT_REPORTING":
      return "◻️️ Not Reporting";
    // Encamp status mappings
    case "AWAITING_VERIFICATION":
      return "💬 Awaiting Verification";
    case "PROCESSING":
      return "🍭 Processing";
    case "FILING_COMPLETE":
      return "🎉 Filing Complete";
    default:
      // For any unknown status, return as-is with a default icon
      return `📋 ${statusToCheck}`;
  }
}

export function ChemicalFacilitiesDataGrid() {
  const navigate = useNavigate();
  const theme = useTheme();
  const omnisearchInputRef = useRef<HTMLInputElement>(null);
  const { omnisearch, sortModel, getOmnisearchValue } = useOmnisearchDatagrid({
    persistenceKey: "chemical-facilities",
    isURLDriven: true,
  });

  const [reportingYear, setReportingYear] = useState<string | undefined>(
    getOmnisearchValue("reportingYear") ?? currentTierIIReportingYear.toString()
  );

  const [selectedRows, setSelectedRows] = useState<Row[]>([]);
  useDebug(selectedRows);

  // Track omnisearch changes to handle scrolling from UnverifiedTable
  const prevOmnisearch = useRef<string>(omnisearch);

  // Scroll to omnisearch when UnverifiedTable navigation is detected
  useEffect(() => {
    if (prevOmnisearch.current !== omnisearch && omnisearch) {
      const hasUnverifiedFilter =
        omnisearch.includes("Unverified:true") ||
        omnisearch.toLowerCase().includes("unverified");
      const hasStateOrOrgFilter =
        omnisearch.includes("State:") ||
        omnisearch.includes("Organization:") ||
        omnisearch.toLowerCase().includes("state:") ||
        omnisearch.toLowerCase().includes("organization:");

      if (
        hasUnverifiedFilter &&
        hasStateOrOrgFilter &&
        omnisearchInputRef.current
      ) {
        // Scroll to omnisearch input and focus it
        setTimeout(() => {
          if (omnisearchInputRef.current) {
            // Get the element's position
            const rect = omnisearchInputRef.current.getBoundingClientRect();
            const headerHeight = 80; // Approximate sticky header height - adjust as needed

            // Scroll to position that accounts for the sticky header
            window.scrollTo({
              top: window.pageYOffset + rect.top - headerHeight,
              behavior: "smooth",
            });

            // Focus the search input so user can immediately start typing
            omnisearchInputRef.current.focus();
          }
        }, 300);
      }
    }
    prevOmnisearch.current = omnisearch;
  }, [omnisearch]);

  const handleSelectedRowsChanged = useCallback((rows: Row[]) => {
    setSelectedRows(rows);
  }, []);

  const defaultSearch = useMemo(() => {
    const omnisearchReportingYear = getOmnisearchValue("reportingYear");

    const search = [];
    if (!omnisearchReportingYear) {
      search.push(`reportingYear:${currentTierIIReportingYear}`);
    }

    if (search.length > 0) {
      return search.join(" ");
    }

    return undefined;
  }, [getOmnisearchValue]);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "organization",
        headerName: "Organization",
        flex: 0.2,
        minWidth: 120,
      },
      {
        field: "facility",
        headerName: "Facility Name",
        flex: 0.25,
        minWidth: 150,
        valueGetter: (_, row) => {
          const { facilityName, streetAddress1, city, zip } = row;
          const address = [streetAddress1, city, zip]
            .filter(Boolean)
            .join(", ");
          return `${facilityName}${address ? ` (${address})` : ""}`;
        },
        renderCell: (params) => {
          const { facilityName, streetAddress1, city, zip } = params.row;
          const address = [streetAddress1, city, zip]
            .filter(Boolean)
            .join(", ");
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{facilityName}</Typography>
              <Typography variant="caption">{address}</Typography>
            </Box>
          );
        },
      },
      {
        field: "state",
        headerName: "State",
        flex: 0.08,
        minWidth: 50,
      },
      {
        field: "customerFacilityId",
        headerName: "Facility ID",
        flex: 0.12,
        minWidth: 80,
      },
      {
        field: "reportStatus",
        headerName: "Report Status",
        flex: 0.15,
        minWidth: 100,
        sortingOrder: ["desc", "asc"],
        valueGetter: (_, row) => {
          return getReportStatusDisplay(
            row.organizationStatus,
            row.encampStatus
          );
        },
        renderCell: (params) => {
          const { organizationStatus, encampStatus } = params.row;
          const displayStatus = getReportStatusDisplay(
            organizationStatus,
            encampStatus
          );

          return <Box sx={{ fontSize: "0.875rem" }}>{displayStatus}</Box>;
        },
      },
      {
        field: "reportKind",
        headerName: "Report Kind",
        flex: 0.1,
        minWidth: 70,
        valueGetter: (_, row) => {
          return row.reportKind || "ANNUAL";
        },
      },
      {
        field: "chemicalCount",
        headerName: "Chemicals",
        flex: 0.08,
        minWidth: 60,
        type: "number",
        sortingOrder: ["desc", "asc"],
        renderCell: (params) => {
          return params.value || params.value === 0 ? params.value : "-";
        },
      },
      {
        field: "partnerTenantName",
        headerName: "Partner",
        flex: 0.15,
        minWidth: 80,
        renderCell: (params) => {
          return params.value || "";
        },
      },
      {
        field: "issues",
        headerName: "Issues",
        flex: 0.08,
        minWidth: 50,
        type: "number",
        sortingOrder: ["desc", "asc"],
        renderCell: (params) => {
          return params.value || params.value === 0 ? params.value : "-";
        },
      },
      {
        field: "county",
        headerName: "County",
        flex: 0.12,
        minWidth: 70,
      },
      {
        field: "fireDepartment",
        headerName: "Fire Dept",
        flex: 0.15,
        minWidth: 80,
        renderCell: (params) => {
          return params.value || "";
        },
      },
      {
        field: "lepc",
        headerName: "LEPC",
        flex: 0.12,
        minWidth: 70,
        renderCell: (params) => {
          return params.value || "";
        },
      },
      {
        field: "fulfillmentNote",
        headerName: "Fulfillment Note",
        flex: 0.2,
        minWidth: 150,
        sortable: false,
        filterable: true,
        renderCell: (params) => {
          return params.value ? (
            <Tooltip
              title={params.value}
              slotProps={{
                tooltip: { sx: { whiteSpace: "pre-wrap" } },
              }}
            >
              <Typography variant="body2" noWrap>
                {params.value}
              </Typography>
            </Tooltip>
          ) : null;
        },
      },
      {
        field: "csmName",
        headerName: "CSM",
        flex: 0.15,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: (params) => {
          const { csmName, csmEmail } = params.row;
          if (!csmName && !csmEmail) return null;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{csmName}</Typography>
              <Typography variant="caption">{csmEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "amName",
        headerName: "Account Manager",
        flex: 0.15,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: (params) => {
          const { amName, amEmail } = params.row;
          if (!amName && !amEmail) return null;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{amName}</Typography>
              <Typography variant="caption">{amEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "dataManagerName",
        headerName: "Data Manager",
        flex: 0.15,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: (params) => {
          const { dataManagerName, dataManagerEmail } = params.row;
          if (!dataManagerName && !dataManagerEmail) return null;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{dataManagerName}</Typography>
              <Typography variant="caption">{dataManagerEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "wasteManagerName",
        headerName: "Waste Manager",
        flex: 0.15,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: (params) => {
          const { wasteManagerName, wasteManagerEmail } = params.row;
          if (!wasteManagerName && !wasteManagerEmail) return null;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{wasteManagerName}</Typography>
              <Typography variant="caption">{wasteManagerEmail}</Typography>
            </Box>
          );
        },
      },
      {
        field: "pocName",
        headerName: "Customer POC",
        flex: 0.15,
        minWidth: 120,
        filterKeyType: "text",
        renderCell: (params) => {
          const { pocName, pocEmail } = params.row;
          if (!pocName && !pocEmail) return null;
          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2">{pocName}</Typography>
              <Typography variant="caption">{pocEmail}</Typography>
            </Box>
          );
        },
      },
    ],
    []
  );

  const filterGroups: FilterGroup[] = useMemo(
    () => [
      // Column 1: Organization + Location
      {
        name: "🏢 Organization & Facility",
        fields: [
          "customerFacilityId",
          "facility",
          "partnerTenantName",
          "tenant",
        ],
      },
      {
        name: "📍 Location",
        fields: ["state", "county"],
      },
      {
        name: "📋 Report Status",
        fields: ["reportKind", "organizationStatus", "encampStatus"],
      },
      // Column 2: Agencies + Account Team
      {
        name: "🚒 Agencies",
        fields: ["fireDepartment", "lepc", "lepcId", "fireDepartmentId"],
      },
      {
        name: "👥 Account Team",
        fields: ["csmName", "amName", "dataManagerName", "wasteManagerName"],
      },
      // Column 3: Customer (Other Filters will follow)
      {
        name: "🤝 Customer",
        fields: ["pocName"],
      },
    ],
    []
  );

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Stack
        sx={{ flexDirection: { xs: "column", sm: "row" } }}
        spacing={2}
        px={3}
        paddingTop={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body1" color={theme.palette.text.secondary}>
          Chemical Facilities
        </Typography>
      </Stack>
      <Box
        sx={{
          height: "100vh",
          pt: 3,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <OmnisearchDataGrid<
          ChemicalFacilitiesQuery,
          ChemicalFacilitiesQueryVariables,
          Row
        >
          omnisearchInputRef={omnisearchInputRef}
          autoFocus={false}
          isURLDriven
          persistenceKey="chemical-facilities-test"
          columns={columns}
          columnVisibilityModel={{
            fulfillmentNote: false,
            csmName: false,
            amName: false,
            dataManagerName: false,
            wasteManagerName: false,
            pocName: false,
          }}
          commandButtons={[
            <Stack
              key="command-buttons"
              id="command-buttons"
              sx={{
                marginBottom: "1rem",
                marginTop: "1rem",
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
              }}
              spacing={2}
              alignItems="center"
            >
              <Stack
                direction={"row"}
                justifyContent={"flex-end"}
                marginTop={0}
                sx={{ flex: { sm: 1, lg: 2 }, alignItems: "center" }}
              >
                <Box sx={{ marginRight: 2 }}>
                  <ExportGridToCsvButton
                    search={omnisearch}
                    sort={[...sortModel]}
                    grid="chemicalFacilities"
                  />
                </Box>
                <ChemicalFacilitiesActionsMenu selectedRows={selectedRows} />
              </Stack>
            </Stack>,
          ]}
          dataQuery={CHEMICAL_FACILITIES_QUERY}
          checkboxSelection
          disableRowSelectionOnClick
          onRowClick={useCallback(
            ({ row }: { row: Row }) => {
              if (row.tierIIReportId) {
                navigate(`/staff/fulfillment/r/${row.tierIIReportId}`);
              } else if (row.facilityId) {
                navigate(
                  `/staff/fulfillment/f/${row.facilityId}/${reportingYear}`
                );
              }
            },
            [navigate, reportingYear]
          )}
          getRowLink={(row) => {
            if (row.tierIIReportId) {
              return `/staff/fulfillment/r/${row.tierIIReportId}`;
            } else if (row.facilityId) {
              return `/staff/fulfillment/f/${row.facilityId}/${reportingYear}`;
            }
            return null;
          }}
          defaultSearch={defaultSearch}
          initialSortModel={[{ field: "facility", sort: "desc" }]}
          getItems={(data) => data.chemicalFacilities.items}
          getCount={(data) => data.chemicalFacilities.count}
          noDataMessage="No chemical facilities found for the current search."
          initialPageSize={50}
          filterGroups={filterGroups}
          wideLayout
          onSelectedRowsChanged={handleSelectedRowsChanged}
          additionalFilterColumns={[
            {
              header: "FacilityPicker",
              key: "facility",
              filterKeyType: "facility",
            },
            {
              header: "Organization",
              key: "tenant",
              filterKeyType: "multipleTenant",
            },
            {
              header: "Report Kind",
              key: "reportKind",
              filterKeyType: "enum",
              enumValues: Object.values(TierIiReportKind),
            },
            {
              header: "Organization Status",
              key: "organizationStatus",
              filterKeyType: "enum",
              enumValues: Object.values(TierIiReportOrgStatus),
            },
            {
              header: "Encamp Status",
              key: "encampStatus",
              filterKeyType: "enum",
              enumValues: Object.values(TierIiReportEncampStatus),
            },
            { header: "FD ID", key: "fireDepartmentId" },
            { header: "LEPC ID", key: "lepcId" },
            { header: "CERS ID", key: "cersId" },
            { header: "STEERS ID", key: "steersId" },
          ]}
          fetchPolicy="no-cache"
          excludeFilterColumns={[
            "reportStatus",
            "reportKind",
            "organization",
            "facility",
          ]}
          gridSx={{
            height: "100%",
            minHeight: "400px",
            maxHeight: "calc(100vh - 280px)", // Account for search controls, filters, and padding
          }}
        />
      </Box>
    </Paper>
  );
}
